/**
 * Redux Toolkit slice for dashboard summaries, announcements, and My Request data.
 * Manages dashboard lookup results, approval filters, pagination state, exports, and reset actions.
 */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import getflattenResponse, { cryptoRand } from '../../helpers/common';
import fieldMapping from '../../constants/fieldMappings/poSearchFieldMapping';
import request from '../../services/request';
import collabReportFieldMapping from '../../constants/fieldMappings/collabReportFieldMapping';
import { myRequestTableHeader } from '../../constants/dataTableHeader';
import myRequestTableFieldProperties from '../../constants/fieldProperties/myRequestTableFieldProperties';
import { mapFiltersToRequest, getFilterValuesFromResponse } from '../../helpers/dataHelpers';
import appConfig from '../../appConfig';
import { settingsActions } from '../profileSettings/settingSlice';
import {
  toggleSpinner
} from '../dialogStates/dialogStatesSlice';
import { conversationThreadsActions } from '../conversationThread/conversationThreadSlice';
import { fileUDActions } from '../fileUD/fileUDSlice';
import { externalLinkActions } from '../externalLink/externalLinkSlice';

const name = 'Dashboard';
function createInitialState() {
  return {
    dashboardResult: [],
    spinner: false,
    requestSpinner: false,
    error: false,
    request: [],
    requestNextResults: {},
    requestPrevResults: {},
    requestTotalCount: 0,
    filterDropdownValues: {},
    myRequestFilterId: 'none',
    selectedFilters: {
      [fieldMapping.PRODUCT_CODE_REQUEST]: [],
      [fieldMapping.DOC_TYPE_CODE]: [],
      [fieldMapping.CURRENT_GAC_REASON_CODE]: [],
      [fieldMapping.VENDOR_CODE_REQUEST]: []
    },
    body: null,
    announcement: {},
    savedSearchView: ''
  };
}
/**
 * Reducer that refreshes the My Request filter id used to signal filter updates.
 */
function updateDashboardRequestFilterId(state) {
  return { ...state, myRequestFilterId: cryptoRand().toString().slice(2, 10) };
}
/**
 * Reducer that stores announcement data displayed on the dashboard.
 */
function dashboardAnnouncement(state, action) {
  return {
    ...state,
    announcement: action.payload
  };
}
/**
 * Reducer that toggles the My Request page spinner state.
 */
function requestPageSpinner(state, action) {
  return {
    ...state,
    requestSpinner: action.payload
  };
}
/**
 * Reducer that stores dashboard summary results.
 */
function dashboardResult(state, action) {
  return {
    ...state,
    dashboardResult: action.payload
  };
}
/**
 * Reducer that toggles the dashboard summary spinner state.
 */
function dashboardSpinner(state, action) {
  return {
    ...state,
    spinner: action.payload
  };
}
/**
 * Reducer that stores the saved search view selected from the dashboard.
 */
function updateSavedSearchView(state, action) {
  return {
    ...state,
    savedSearchView: action.payload,
    comingFromDashboard: !!action.payload
  };
}
/**
 * Reducer that clears the dashboard navigation flag.
 */
function setComingFromDashboard(state) {
  return {
    ...state,
    comingFromDashboard: false
  };
}

/**
 * Reducer that stores dashboard errors and stops the summary spinner.
 */
function dashboardFailure(state, action) {
  return {
    ...state,
    error: action.payload,
    spinner: false
  };
}
/**
 * Reducer that updates one My Request selected filter value array.
 */
function addAllFilterValue(state, action) {
  // Ignore malformed filter payloads so existing selections remain intact.
  if (Array.isArray(action.payload.values) && action.payload.filter) {
    return {
      ...state,
      selectedFilters: {
        ...state.selectedFilters,
        [action.payload.filter]: action.payload.values
      }
    };
  }
  return { ...state };
}
/**
 * Reducer that writes mapped approval filters into the current request body.
 */
function updateApprovalsFiltersInRequest(state, action) {
  const updatedBody = { ...state.body, filter: action.payload };
  return { ...state, body: updatedBody };
}
/**
 * Reducer that stores the active My Request search request body.
 */
function updateSearchRequestBody(state, action) {
  return {
    ...state,
    body: action.payload
  };
}
/**
 * Reducer that clears dashboard result rows and filter dropdown values.
 */
function resetFilterToInitialState(state) {
  return {
    ...state,
    dashboardResult: [],
    filterDropdownValues: {}
  };
}
/**
 * Reducer that stores My Request filter dropdown values returned by search APIs.
 */
function requestUpdateFilterDropdownValues(state, action) {
  if (
    action.payload
    && Object.keys(action.payload).length > 0
  ) {
    return {
      ...state,
      filterDropdownValues: action.payload
    };
  }
  return { ...state };
}

/**
 * Reducer that stores the current My Request page, offsets, totals, and cached result state.
 */
function dashboardRequestPage(state, action) {
  return {
    ...state,
    request: action.payload.objects,
    dashboardMyRequestPrevOffset: action.payload.pages.prev,
    dashboardMyRequestNextOffset: action.payload.pages.next,
    selectedRecordsCount: 0,
    requestTotalCount: action.payload.pages.totalResources,
    dashboardMyRequestNextResults: null,
    dashboardMyRequestPrevResults: null
  };
}
/**
 * Reducer that stores fetched next-page My Request data and pagination offsets.
 */
function updateDashboardMyRequestNextResult(state, action) {
  return {
    ...state,
    request: action.payload.data,
    requestNextResults: action.payload.dataNext.objects,
    dashboardMyRequestPrevOffset: action.payload.dataNext.pages.prev,
    dashboardMyRequestNextOffset: action.payload.dataNext.pages.next,
    dashboardMyRequestTotalPages: action.payload.dataNext.pages.totalPages,
    requestTotalCount: action.payload.dataNext.pages.totalResources
  };
}
/**
 * Reducer that appends cached next approval results into the current request list.
 */
function updateApprovalNextResultsAppend(state, action) {
  return {
    ...state,
    request: action.payload.data,
    requestPrevResults: action.payload.dataPrev,
    requestNextResults: null
  };
}
/**
 * Reducer that stores fetched previous-page My Request data and pagination offsets.
 */
function updateDashboardApprovalPrevResult(state, action) {
  return {
    ...state,
    request: action.payload.data,
    requestPrevResults: action.payload.dataPrev.objects,
    dashboardMyRequestPrevOffset: action.payload.dataPrev.pages.prev,
    dashboardMyRequestNextOffset: action.payload.dataPrev.pages.next,
    dashboardMyRequestTotalPages: action.payload.dataPrev.pages.totalPages,
    requestTotalCount: action.payload.dataPrev.pages.totalResources

  };
}
/**
 * Reducer that appends cached previous approval results into the current request list.
 */
function updateApprovalPrevResultsAppend(state, action) {
  return {
    ...state,
    request: action.payload.data,
    requestNextResults: action.payload.dataNext,
    requestPrevResults: null
  };
}
/**
 * Reducer that stores the approval continuation token.
 */
function updateApprovalContinuationToken(state, action) {
  return {
    ...state,
    continuationToken: action.payload
  };
}
/**
 * Reducer that moves cached previous results into the current My Request page.
 */
function appendPrevResultSet(state) {
  const updatedCurrentResultSet = state.request;
  const updatedPrevResultSet = state.requestPrevResults;
  const payload = {
    data: updatedCurrentResultSet,
    dataNext: updatedPrevResultSet
  };

  return {
    ...state,
    request: payload.data,
    requestNextResults: payload.dataNext,
    requestPrevResults: null
  };
}
/**
 * Reducer that moves cached next results into the current My Request page.
 */
function appendDashboardNextResultSet(state) {
  const updatedCurrentResultSet = state.request;
  const updatedNextResultSet = state.requestNextResults;
  const payload = {
    data: updatedCurrentResultSet,
    dataNext: updatedNextResultSet
  };

  return {
    ...state,
    request: payload.data,
    requestPrevResults: payload.dataPrev,
    requestNextResults: null
  };
}

/**
 * Builds the reducer map consumed by the dashboard slice.
 */
function createReducers() {
  return {
    resetFilterToInitialState,
    requestUpdateFilterDropdownValues,
    dashboardRequestPage,
    updateDashboardRequestFilterId,
    dashboardSpinner,
    dashboardFailure,
    updateApprovalNextResultsAppend,
    dashboardResult,
    dashboardAnnouncement,
    requestPageSpinner,
    addAllFilterValue,
    updateApprovalContinuationToken,
    updateApprovalPrevResultsAppend,
    updateDashboardApprovalPrevResult,
    updateDashboardMyRequestNextResult,
    updateApprovalsFiltersInRequest,
    updateSearchRequestBody,
    dashboardMyRequestOffset: dashboardRequestPage,
    appendPrevResultSet,
    appendDashboardNextResultSet,
    updateSavedSearchView,
    setComingFromDashboard
  };
}

export const initialState = createInitialState();
export const reducers = createReducers();
const slice = createSlice({ name, initialState, reducers });

const actions = { ...slice.actions };
/**
 * Maps a selected My Request filter key to the API filtersInResponse descriptor.
 */
const filterFunc = (filter) => {
  const filterInResponse = {};
  // Use table field metadata to tell the API which filter values to return.
  const fieldProperties = myRequestTableFieldProperties[filter] || {};
  if (!fieldProperties) {
    return null;
  }
  filterInResponse.primary = fieldProperties.primary;
  if (fieldProperties.secondary) {
    filterInResponse.secondary = fieldProperties.secondary;
  }
  return filterInResponse;
};
/**
 * Async thunk that calls DASHBOARD and stores dashboard summary results.
 */
function getDashboardResults() {
  return createAsyncThunk(
    `${name}/getDashboardResults`,
    async (_, { dispatch }) => {
      dispatch(actions.dashboardSpinner(true));
      request({
        api: 'DASHBOARD',
        method: 'get'
      }, dispatch).then((response) => {
        if (Object.keys(response.data).length === 0) {
          console.log('No data found in dashboard');
        } else {
          dispatch(actions.dashboardSpinner(false));
          dispatch(actions.dashboardResult(response.data));
        }
      }).catch((error) => {
        console.log('Error: ', error);
        dispatch(actions.dashboardFailure(error));
      });
    }
  );
}
/**
 * Async thunk that calls ANNOUNCEMENT and stores dashboard announcement text.
 */
function getDashboardAnnouncementData() {
  return createAsyncThunk(
    `${name}/getDashboardAnnouncementData`,
    async (_, { dispatch }) => {
      request({
        api: 'ANNOUNCEMENT',
        method: 'get'
      }, dispatch).then((response) => {
        dispatch(actions.dashboardAnnouncement(response?.data.announcement));
      }).catch((error) => {
        console.log('Error: ', error);
      });
    }
  );
}
/**
 * Async thunk that calls collaborationReportSearch for the My Request table.
 */
function fetchDashboardRequestResults() {
  return createAsyncThunk(
    `${name}/fetchDashboardRequestResults`,
    async (props, { getState, dispatch }) => {
      const { statusCode, searchtype, callback } = props;
      dispatch(actions.resetFilterToInitialState());
      dispatch(actions.requestPageSpinner(true));
      const state = getState();
      const fields = [];
      // Build the field list from the table header configuration used by My Request.
      myRequestTableHeader.forEach((headRow) => {
        fields.push(headRow.primary);
        const secondaryFields = {};
        if (headRow.secondary && !secondaryFields[headRow.secondary]) {
          secondaryFields[headRow.secondary] = true;
          fields.push(headRow.secondary);
        }
      });
      const requestBody = {
        search: [{
          fieldName: 'poData.sizes.changeStatusCode',
          operator: '=',
          fieldValue: statusCode
        }],
        fields: [...fields, collabReportFieldMapping.REPORT_ISLINE],
        filter: [],
        // Request dropdown values only for filters that are currently enabled.
        filtersInResponse: Object.keys(state.Dashboard.selectedFilters)
          .filter((filter) => state.Dashboard.selectedFilters[filter])
          .map((filter) => filterFunc(filter))
          .filter((element) => element),
        count: appConfig.SearchThresholdLimit,
        offset: 0,
        searchType: searchtype
      };
      // Overdue GAC requests are pending requests with the overdue indicator set.
      if (statusCode === 'OverDueGAC') {
        requestBody.search = [{
          fieldName: 'poData.sizes.changeStatusCode',
          operator: '=',
          fieldValue: 'Pending'
        },
        {
          fieldName: 'poData.sizes.gacOverdueIndicator',
          operator: '=',
          fieldValue: 'Yes'
        }
        ];
      }
      request({
        api: 'collaborationReportSearch',
        method: 'post',
        data: requestBody
      }, dispatch).then((response) => {
        if (response) {
          dispatch(actions.requestPageSpinner(false));
          dispatch(actions.updateSearchRequestBody(requestBody));
          dispatch(actions.dashboardRequestPage(getflattenResponse(response.data)));
          dispatch(actions.requestUpdateFilterDropdownValues(
            getFilterValuesFromResponse(response.data.filters)
          ));
          callback(response);
        }
      }).catch((error) => {
        console.log('Error: ', error);
        dispatch(actions.requestPageSpinner(false));
        if (callback) {
          callback(null, error);
        }
      });
    }
  );
}
/**
 * Async thunk that calls POAPPROVEORREJECT to approve or reject selected requests.
 */
function approveOrRejectRequest() {
  return createAsyncThunk(
    `${name}/approveOrRejectRequest`,
    async ({
      statusCode, type, ids, approveOrRejectCallback
    }, { getState, dispatch }) => {
      const state = getState();
      dispatch(actions.requestPageSpinner(true));
      const requestBody = {
        ...state.Dashboard.body,
        changeStatusCode: statusCode,
        search: state.Dashboard.body && Array.isArray(state.Dashboard.body.search)
          ? [...state.Dashboard.body.search]
          : []
      };
      // Bulk updates exclude or include selected size ids based on the action type flag.
      if (type && ids.length > 0) {
        requestBody.search.push(
          {
            fieldName: 'poData.sizes.sizeId',
            operator: type ? '!=' : '=',
            fieldValue: ids
          }
        );
      }
      request({
        api: 'POAPPROVEORREJECT',
        method: 'put',
        data: type ? requestBody : { ...ids, changeStatusCode: statusCode }
      }, dispatch).then((response) => {
        if (response) {
          dispatch(actions.requestPageSpinner(false));
          approveOrRejectCallback(response);
        }
      }).catch((error) => {
        dispatch(actions.requestPageSpinner(false));
        if (approveOrRejectCallback) {
          approveOrRejectCallback(null, error);
        }
      });
    }
  );
}
/**
 * Async thunk that calls collaborationReportExport for the current My Request search.
 */
function exportPoCollab() {
  return createAsyncThunk(
    `${name}/exportPoCollab`,
    async ({
      poCollabCallback, fileType
    }, { getState, dispatch }) => {
      const state = getState();
      const poCollabSearchRequestBody = {
        ...state.Dashboard.body
      };
      const poCollabRequestBody = {
        search: [...(poCollabSearchRequestBody.search) || []],
        fields: [...(poCollabSearchRequestBody.fields) || []].filter((field) => (
          !appConfig.excludedPOReportFieldsInExportedFile.includes(field)
        )),
        filter: [...(poCollabSearchRequestBody.filter) || []],
        searchType: poCollabSearchRequestBody.searchType,
        fileFormat: fileType
      };
      request({
        api: 'collaborationReportExport',
        method: 'post',
        data: poCollabRequestBody
      }, dispatch).then((response) => {
        if (response) {
          poCollabCallback(response);
        }
      }).catch((error) => {
        if (poCollabCallback) {
          poCollabCallback(null, error);
        }
      });
    }
  );
}
/**
 * Async thunk that applies My Request filters and reruns collaborationReportSearch.
 */
function applyFilters() {
  return createAsyncThunk(
    `${name}/applyFilters`,
    async (props, { getState, dispatch }) => {
      const {
        payload,
        applyFiltersCallback,
        shouldRequestFilterValues
      } = props;
      dispatch(actions.requestPageSpinner(true));
      dispatch(actions.addAllFilterValue(payload));
      let state = getState();
      // Convert Redux selected filter arrays into the API filter request shape.
      const filtersRequest = mapFiltersToRequest(state.Dashboard.selectedFilters);
      dispatch(actions.updateApprovalsFiltersInRequest(filtersRequest));
      state = getState();
      const requestBody = { ...state.Dashboard.body };
      const selectedFilterKeys = Object.keys(state.Dashboard.selectedFilters)
        .filter((filter) => state.Dashboard.selectedFilters[filter]);
      if (shouldRequestFilterValues && selectedFilterKeys.length > 0) {
        // Ask the API to refresh available values for the active filters.
        requestBody.filtersInResponse = selectedFilterKeys.map((filter) => filterFunc(filter))
          .filter((element) => element);
      } else {
        delete requestBody.filtersInResponse;
      }
      request({
        api: 'collaborationReportSearch',
        method: 'post',
        data: requestBody
      }, dispatch).then((response) => {
        dispatch(actions.requestPageSpinner(false));
        if (response.data.filters) {
          dispatch(actions.requestUpdateFilterDropdownValues(
            getFilterValuesFromResponse(response.data.filters)
          ));
        }
        dispatch(actions.updateDashboardRequestFilterId());
        dispatch(actions.updateSearchRequestBody(requestBody));
        dispatch(actions.dashboardRequestPage(getflattenResponse(response.data)));
        if (applyFiltersCallback) {
          applyFiltersCallback(response);
        }
      }).catch((error) => {
        dispatch(actions.requestPageSpinner(false));
        if (applyFiltersCallback) {
          applyFiltersCallback(null, error);
        }
      });
    }
  );
}
/**
 * Async thunk that reloads My Request data from offset zero.
 */
function fetchMyDashboardRequestResultsOffset() {
  return createAsyncThunk(
    `${name}/fetchMyDashboardRequestResultsOffset`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      if (state.Dashboard.body && state.Dashboard.request) {
        const updatedRequest = state.Dashboard.body;
        delete updatedRequest.offset;
        updatedRequest.offset = '0';
        dispatch(toggleSpinner(true));
        request({
          api: 'collaborationReportSearch',
          method: 'post',
          data: updatedRequest
        }, dispatch, getState).then((response) => {
          dispatch(actions.dashboardMyRequestOffset(getflattenResponse(response.data)));
          dispatch(toggleSpinner(false));
        }).catch((error) => {
          console.log('Error: ', error);
          dispatch(toggleSpinner(false));
        });
      }
    }
  );
}
/**
 * Async thunk that dispatches the provided next-page fetch thunk.
 */
function dashboardMyRequestDataNextSet() {
  return createAsyncThunk(
    `${name}/dashboardMyRequestDataNextSet`,
    async (
      { dashboardfetchReportNextResultSet },
      { dispatch }
    ) => {
      dispatch(dashboardfetchReportNextResultSet());
    }
  );
}
/**
 * Async thunk that fetches the next My Request result set using the stored next offset.
 */
function fetchReportNextResultSet() {
  return createAsyncThunk(
    `${name}/fetchReportNextResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      if (state.Dashboard.body && state.Dashboard.request
        && state.Dashboard.dashboardMyRequestNextOffset) {
        const currentResultSet = state.Dashboard.request;
        const updatedRequest = { ...state.Dashboard.body }; // clone to avoid mutation
        const searchResultNextOffset = state.Dashboard.dashboardMyRequestNextOffset;
        delete updatedRequest.offset;
        const resNext = searchResultNextOffset.indexOf(',');
        // Extract the numeric offset from the API pagination link.
        updatedRequest.offset = searchResultNextOffset.slice(7, resNext);

        request({
          api: 'collaborationReportSearch',
          method: 'post',
          data: updatedRequest
        }, dispatch, getState).then((response) => {
          dispatch(actions.updateSearchRequestBody(updatedRequest));
          dispatch(toggleSpinner(false));
          dispatch(actions.updateDashboardMyRequestNextResult(response, currentResultSet));
        }).catch((error) => {
          console.log('Error: ', error);
        });
      }
    }
  );
}
/**
 * Async thunk that resets dashboard filter state when navigating away from the dashboard.
 */
function resetDashboard() {
  return createAsyncThunk(
    `${name}/resetDashboard`,
    (currentPage, { getState, dispatch }) => {
      const state = getState();
      const dashboardResultData = state?.Dashboard?.dashboardResult;
      // Accept either a route key string or a navigation payload object.
      const page = typeof currentPage === 'string' ? currentPage : currentPage?.currentPage;
      if (dashboardResultData?.id != null && page !== 'DASHBOARD') {
        dispatch(actions.resetFilterToInitialState());
      }
    }
  );
}
/**
 * Async thunk that resets dashboard-related Redux slices for page navigation.
 */
function resetAllReduxState() {
  return createAsyncThunk(
    `${name}/resetAllReduxState`,
    (currentPage, { dispatch }) => {
      dispatch(resetDashboard()(currentPage));
      // Keep dashboard navigation resets in sync across related feature slices.
      dispatch(conversationThreadsActions.resetConversation(currentPage));
      dispatch(fileUDActions.resetBulkUpload(currentPage));
      dispatch(externalLinkActions.resetExternalLink(currentPage));
    }
  );
}

/**
 * Async thunk that dispatches the provided previous-page fetch thunk.
 */
function dashboardDataPrevSet() {
  return createAsyncThunk(
    `${name}/dashboardDataPrevSet`,
    async (
      { dashboardfetchReportPrevResultSet },
      { dispatch }
    ) => {
      dispatch(dashboardfetchReportPrevResultSet());
    }
  );
}
/**
 * Async thunk that fetches the previous My Request result set using the stored previous offset.
 */
function fetchReportPrevResultSet() {
  return createAsyncThunk(
    `${name}/fetchReportPrevResultSet`,
    async (_, { getState, dispatch }) => {
      const state = getState();
      if (state.Dashboard.body && state.Dashboard.request
        && state.Dashboard.dashboardMyRequestPrevOffset) {
        const currentResultSet = state.Dashboard.request;
        const updatedRequest = { ...state.Dashboard.body };
        const searchResultPrevOffset = state.Dashboard.dashboardMyRequestPrevOffset;
        delete updatedRequest.offset;
        const res = searchResultPrevOffset.indexOf(',');
        // Extract the numeric offset from the API pagination link.
        updatedRequest.offset = searchResultPrevOffset.slice(7, res);
        request({
          api: 'collaborationReportSearch',
          method: 'post',
          data: updatedRequest
        }, dispatch, getState).then((response) => {
          dispatch(actions.updateSearchRequestBody(updatedRequest));
          dispatch(toggleSpinner(false));
          dispatch(actions.updateDashboardApprovalPrevResult(response, currentResultSet));
        }).catch((error) => {
          console.log('PREV Error ', error);
        });
      }
    }
  );
}
/**
 * Async thunk that dispatches the provided offset-reset fetch thunk.
 */
function dashboardMyRequestDataOffset() {
  return createAsyncThunk(
    `${name}/dashboardMyRequestDataOffset`,
    async (
      { dashboardfetchMyDashboardRequestResultsOffset },
      { dispatch }
    ) => {
      dispatch(dashboardfetchMyDashboardRequestResultsOffset());
    }
  );
}

/**
 * Async thunk that creates a user profile and reloads the page when successful.
 */
function createUserProfile() {
  return createAsyncThunk(
    `${name}/createUserProfile`,
    async (_, { dispatch }) => {
      dispatch(settingsActions.createUserProfile((profile) => {
        if (profile) {
          window.location.reload();
        } else {
          dispatch(toggleSpinner(false));
          dispatch(actions.dashboardSpinner(false));
        }
      }));
    }
  );
}

/**
 * Creates async thunk action creators exposed by the dashboard slice.
 */
function createExtraActions() {
  return {
    dashboardMyRequestDataNextSet: dashboardMyRequestDataNextSet(),
    getDashboardResults: getDashboardResults(),
    getDashboardAnnouncementData: getDashboardAnnouncementData(),
    fetchDashboardRequestResults: fetchDashboardRequestResults(),
    approveOrRejectRequest: approveOrRejectRequest(),
    exportPoCollab: exportPoCollab(),
    applyFilters: applyFilters(),
    fetchMyDashboardRequestResultsOffset: fetchMyDashboardRequestResultsOffset(),
    fetchReportNextResultSet: fetchReportNextResultSet(),
    resetAllReduxState: resetAllReduxState(),
    resetDashboard: resetDashboard(),
    dashboardDataPrevSet: dashboardDataPrevSet(),
    fetchReportPrevResultSet: fetchReportPrevResultSet(),
    dashboardMyRequestDataOffset: dashboardMyRequestDataOffset(),
    createUserProfile: createUserProfile()
  };
}

export const extraActions = createExtraActions();

export const dashboardActions = { ...actions, ...extraActions };
export const dashboardReducer = slice.reducer;
